import React, { useEffect, useState } from "react"
import { useTranslation } from 'react-i18next'
import { Modal } from 'react-bootstrap'

import { formatDate, formatMoney } from 'helpers/helpers'
import { httpOk, GET } from 'helpers/api'

// История правок документа: что меняли, кто и с какой причиной.
// Один компонент на все типы документов — тип передаётся кодом таблицы (entity),
// как его понимает /document-revisions на бэке.
//
// Показываем только diff: полный снимок «как было» лежит в базе и нужен для разбора,
// а человеку в модалке важно увидеть именно изменение — «Поставщик: Иванов → Петров».
function DocumentRevisions({ entity, documentId, setDocumentId }) {
	const { t } = useTranslation()

	const [revisions, setRevisions] = useState([])

	async function getRevisions() {
		const response = await GET(`/document-revisions/${entity}/${documentId}`, {}, { loader: true })
		if (httpOk(response)) setRevisions(response.data?.data ?? [])
	}

	function toggleModal() {
		setRevisions([])
		setDocumentId(null)
	}

	// diff шапки приходит как { поле: { label, from, to } }, позиции — отдельным ключом
	function headerChanges(diff) {
		if (!diff) return []

		return Object.keys(diff)
			.filter(key => key !== 'items')
			.map(key => diff[key])
			.filter(change => !!change?.label)
	}

	function itemChanges(diff) {
		const items = diff?.items
		if (!items) return []

		const rows = []

		for (const item of items.added ?? []) {
			rows.push({ 'type': 'added', 'name': item.name, 'text': `${formatMoney(item.quantity)} × ${formatMoney(item.price)}` })
		}
		for (const item of items.removed ?? []) {
			rows.push({ 'type': 'removed', 'name': item.name, 'text': `${formatMoney(item.quantity)} × ${formatMoney(item.price)}` })
		}
		for (const item of items.changed ?? []) {
			const parts = []
			if (item.fields?.quantity) {
				parts.push(`${t('quantity')}: ${formatMoney(item.fields.quantity.from)} → ${formatMoney(item.fields.quantity.to)}`)
			}
			if (item.fields?.price) {
				parts.push(`${t('price')}: ${formatMoney(item.fields.price.from)} → ${formatMoney(item.fields.price.to)}`)
			}
			rows.push({ 'type': 'changed', 'name': item.name, 'text': parts.join(' · ') })
		}

		return rows
	}

	useEffect(() => {
		if (entity && documentId) getRevisions()
	}, [entity, documentId]) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			{/* MODAL DOCUMENT REVISIONS */}
			<Modal show={!!documentId} animation={false} centered size="lg"
				onHide={() => toggleModal()}>
				<Modal.Header closeButton>
					<Modal.Title>{t('document_revisions')}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{revisions.length === 0 &&
						<div className="text-center">{t('no_data')}</div>
					}

					{revisions.map((revision, index) => (
						<div className="border rounded p-2 mb-2" key={index}>
							<div className="d-flex justify-content-between mb-1">
								<b>{t('edit')} №{revision.revision_no - 1}</b>
								<span className="text-nowrap">
									{revision.created_by} · {formatDate(revision.created_at, 'dd.MM.yy HH:mm')}
								</span>
							</div>

							{!!revision.reason &&
								<div className="text-muted mb-1">{t('reason')}: {revision.reason}</div>
							}

							{headerChanges(revision.diff).map((change, changeIndex) => (
								<div key={changeIndex}>
									{change.label}: <span className="text-danger">{String(change.from ?? '—')}</span>
									{' → '}
									<span className="text-success">{String(change.to ?? '—')}</span>
								</div>
							))}

							{itemChanges(revision.diff).map((row, rowIndex) => (
								<div key={`item-${rowIndex}`}>
									{row.type === 'added' && <span className="text-success">+ </span>}
									{row.type === 'removed' && <span className="text-danger">− </span>}
									{row.type === 'changed' && <span className="text-warning">± </span>}
									{row.name} {row.text ? `— ${row.text}` : ''}
								</div>
							))}
						</div>
					))}
				</Modal.Body>
			</Modal>
			{/* MODAL DOCUMENT REVISIONS */}
		</>
	)
}

export default DocumentRevisions
